import { z } from 'zod'

import type { SavedViewSection } from './types'

/**
 * Frontend copy of the SavedView filter schemas.
 *
 * The backend (`@buffer-mono/entities`) owns the canonical source. This file is
 * a structural duplicate so `publish-frontend` doesn't import a backend
 * package. Any change to the entity schema must be mirrored here — enforced by
 * code review, not the build.
 */

const Hex24Re = /^[a-f0-9]{24}$/i

const ObjectIdHexStringSchema = z
  .string()
  .regex(Hex24Re, 'Invalid ObjectId hex string')

const DateString = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD')
  .refine(
    (s) => {
      const date = new Date(`${s}T00:00:00Z`)
      if (Number.isNaN(date.getTime())) return false
      return date.toISOString().slice(0, 10) === s
    },
    { message: 'Invalid calendar date' },
  )

export const PublishFiltersSchema = z
  .object({
    channelIds: z.array(ObjectIdHexStringSchema).min(1).optional(),
    tagIds: z.array(ObjectIdHexStringSchema).min(1).optional(),
    status: z.enum(['queue', 'sent', 'drafts', 'approvals']).optional(),
    date: DateString.optional(),
    granularity: z.enum(['week', 'month', 'three-day']).optional(),
  })
  .strict()

export const CreateFiltersSchema = z
  .object({
    tagIds: z.array(ObjectIdHexStringSchema).min(1).optional(),
    groupIds: z.array(ObjectIdHexStringSchema).min(1).optional(),
  })
  .strict()

export const CommunityFiltersSchema = z
  .object({
    channelIds: z.array(ObjectIdHexStringSchema).min(1).optional(),
    status: z
      .enum(['all', 'unreplied', 'replied', 'dismissed', 'new'])
      .optional(),
    dateRange: z
      .enum([
        // Views saved before the retention window still hold this.
        'all-time',
        'last-7-days',
        'last-30-days',
        'last-6-months',
        'last-year',
      ])
      .optional(),
    sort: z.enum(['asc', 'desc']).optional(),
  })
  .strict()

export const InsightsFiltersSchema = z
  .object({
    channelIds: z.array(ObjectIdHexStringSchema).min(1).optional(),
    dateRange: z
      .enum([
        'last-7-days',
        'last-30-days',
        'month-to-date',
        'last-month',
        'custom',
      ])
      .optional(),
    startDate: DateString.optional(),
    endDate: DateString.optional(),
    // Presets only: a custom comparison range is not yet user-selectable, so it
    // is intentionally excluded rather than accepted and stored.
    comparison: z
      .enum(['previous-period', 'previous-month', 'previous-year'])
      .optional(),
  })
  .strict()

export type PublishFilters = z.infer<typeof PublishFiltersSchema>
export type CreateFilters = z.infer<typeof CreateFiltersSchema>
export type CommunityFilters = z.infer<typeof CommunityFiltersSchema>
export type InsightsFilters = z.infer<typeof InsightsFiltersSchema>

export type SectionFilters =
  | PublishFilters
  | CreateFilters
  | CommunityFilters
  | InsightsFilters

const FilterShapeForSection = {
  publish: PublishFiltersSchema,
  create: CreateFiltersSchema,
  community: CommunityFiltersSchema,
  insights: InsightsFiltersSchema,
} satisfies Record<SavedViewSection, z.ZodSchema<SectionFilters>>

export type ValidateFilterShapeResult =
  | { success: true; data: SectionFilters }
  | { success: false; error: z.ZodError }

/**
 * Validates a serialized filter payload against the schema for the given section.
 */
export function validateFilterShape(
  section: SavedViewSection,
  filters: unknown,
): ValidateFilterShapeResult {
  const result = FilterShapeForSection[section].safeParse(filters)
  if (result.success) {
    return { success: true, data: result.data }
  }
  return { success: false, error: result.error }
}
